import Flux from 'flux';
import FluxUtils from 'flux/utils';
import Immutable from 'immutable';

import type { ActionType, ActionsType } from 'sharedreact/actions/generic/actions';
import AllItemsConstants from 'sharedreact/constants/AllItemsConstants';
import UserPreferencesConstants from 'sharedreact/constants/UserPreferencesConstants';
import type { ItemsConstantsType } from 'sharedreact/constants/generic/Constants';
import { FilterMode } from 'sharedreact/constants/generic/Constants';
import Filter from 'sharedreact/data/Filter';
import GroupAndCount from 'sharedreact/data/GroupAndCount';
import SortOrder from 'sharedreact/data/SortOrder';
import type UpdateAction from 'sharedreact/data/UpdateAction';
import ItemsState, { ItemType, getInitialItemState } from 'sharedreact/stores/generic/ItemsState';
import { compareValues } from 'sharedreact/utils/CompareSupport';
import FilterChecker from 'sharedreact/utils/FilterChecker';

function _compare (firstParameter: unknown, secondParameter: unknown, ascending: boolean): number {
  const compareResult: number = compareValues(firstParameter, secondParameter);
  return (ascending ? compareResult : -compareResult);
}

class ItemsStore<Item extends ItemType, ExtraData = null, Actions extends ActionsType = ActionsType> extends FluxUtils.ReduceStore<ItemsState<Item, ExtraData>, ActionType> {
  _removedIds: string[];
  _canExpandState: boolean;

  _isStoreActive: boolean;
  _isStoreDirty: boolean;

  Actions: Actions;
  Constants: ItemsConstantsType;

  constructor (Dispatcher: Flux.Dispatcher<ActionType>, Actions: Actions, Constants: ItemsConstantsType) {
    super(Dispatcher);

    this.Actions = Actions;
    this.Constants = Constants;

    this._removedIds = [];
    this._canExpandState = true;

    this._isStoreActive = true;
    this._isStoreDirty = false;
  }

  isActive (): boolean {
    return this._isStoreActive;
  }

  _refresh (state: ItemsState<Item, ExtraData>): ItemsState<Item, ExtraData> {
    state = state.set('hasReceivedInitialData', false)
      .set('items', state.items.clear())
      .set('mappedItems', state.mappedItems.clear());

    if (!state.isFetching && state.hasNotFetchedYet) {
      return state;
    }

    return this._fetchAll(state);
  }

  _fetchAll (state: ItemsState<Item, ExtraData>): ItemsState<Item, ExtraData> {
    if (this._isStoreActive) {
      this._getAll();
      return state.set('isFetching', true)
          .set('hasNotFetchedYet', false);
    }
    return state;
  }

  _getAll (): void {
    this.Actions.fetch();
  }

  getInitialSortOrder (): SortOrder {
    return new SortOrder({keyword: '', ascending: true});
  }

  override getInitialState (): ItemsState<Item, ExtraData> {
    return getInitialItemState<Item, ExtraData>(this.getInitialSortOrder());
  }

  _getCompareFunction (sortOrder: SortOrder): (a: Item, b: Item) => number {
    const sortKeyword: string = this.getItemKeyForKeyword(sortOrder.keyword) || sortOrder.keyword;
    const secondaryKeyword: string = this.getItemKeyForKeyword(this.getSecondarySortKeyword()) || this.getSecondarySortKeyword();
    const preSortOrder: string | SortOrder | null = this._getPreSortOrder();

    let optionalPresortKeyword: string | null = null;
    let presortAscending: boolean = true;
    if (preSortOrder instanceof SortOrder) {
      optionalPresortKeyword = preSortOrder.keyword;
      presortAscending = preSortOrder.ascending;
    } else if (typeof preSortOrder === 'string') {
      optionalPresortKeyword = preSortOrder;
      presortAscending = sortOrder.ascending;
    }

    return (firstTemplate: Item, secondTemplate: Item) => {
      let compareResult: number = 0;
      if (optionalPresortKeyword != null && optionalPresortKeyword !== sortKeyword) {
        const primaryKeyword: string = optionalPresortKeyword;
        const firstValue = this.getValue(firstTemplate, primaryKeyword);
        const secondValue = this.getValue(secondTemplate, primaryKeyword);

        compareResult = _compare(firstValue, secondValue, presortAscending);
      }

      if (compareResult === 0) {
        const firstValue = this.getValue(firstTemplate, sortKeyword);
        const secondValue = this.getValue(secondTemplate, sortKeyword);

        compareResult = _compare(firstValue, secondValue, sortOrder.ascending);

        if (compareResult === 0 && sortKeyword !== secondaryKeyword) {
          const firstSecondaryValue = this.getValue(firstTemplate, secondaryKeyword);
          const secondSecondaryValue = this.getValue(secondTemplate, secondaryKeyword);

          compareResult = _compare(firstSecondaryValue, secondSecondaryValue, true);
        }
      }

      return compareResult;
    };
  }

  _sortItems (items: Immutable.List<Item>, sortOrder: SortOrder | null): Immutable.List<Item> {
    if (sortOrder) {
      const compareFunction = this._getCompareFunction(sortOrder);
      if (compareFunction) {
        return items.sort(compareFunction);
      }
    }

    return items;
  }

  _filterItems (items: Immutable.List<Item>, filter: Filter): Immutable.List<Item> {
    const checker = new FilterChecker(this.getFilterKeywords(), (keyword: string) => this.getItemKeyForKeyword(keyword), this.getStateNames(), (item: Item, itemKey: string) => this.getValue(item, itemKey));
    return items.filter((item) => checker.itemConformsToFilter(item, filter, this._canExpandState));
  }

  _generateCountPerGroup (items: Immutable.List<Item>, sortOrder: SortOrder | null): Immutable.List<GroupAndCount> {
    let countPerGroup: GroupAndCount[] = [];

    if (sortOrder) {
      let groupingKeywords: string[] = this.getGroupingKeywords();
      const sortProperty: string | null = this.getItemKeyForKeyword(sortOrder.keyword);
      if (groupingKeywords.includes(sortOrder.keyword) && sortProperty) {
        let index = -1;
        for (const item of items) {
          if (index === -1 ||
            item.get(sortProperty) !== countPerGroup[index].name) {
            // starting with a new group
            countPerGroup.push(new GroupAndCount({
              name: item.get(sortProperty),
              count: 1
            }));
            index++;
          } else {
            // the item is part of the current group
            countPerGroup[index] = countPerGroup[index].set('count', countPerGroup[index].count + 1);
          }
        }
      }
    }

    return Immutable.List(countPerGroup);
  }

  isFetching () {
    return this.getState().isFetching;
  }

  hasNotFetchedYet () {
    return this.getState().hasNotFetchedYet;
  }

  hasReceivedInitialData () {
    return this.getState().hasReceivedInitialData;
  }

  getSortKeywords (): string[] {
    return [];
  }

  getSecondarySortKeyword (): string {
    return this.getInitialSortOrder().keyword;
  }

  _getPreSortOrder (): string | SortOrder | null {
    return null;
  }

  getItems (): Immutable.List<Item> {
    return this.getState().items;
  }

  getTotalNumberOfItems (): number {
    return this.getState().items.size;
  }

  getCountPerGroup (): Immutable.List<GroupAndCount> {
    return this.getState().countPerGroup;
  }

  getFilter (): Filter {
    return this.getState().filter;
  }

  getFilterString (): string {
    return this.getState().filterString;
  }

  getFilterKeywords (): string[] {
    return [];
  }

  getStateNames (): {name: string, value: string}[] | null {
    return null;
  }

  getGroupingKeywords (): string[] {
    return [];
  }

  getSortOrder (): SortOrder {
    return this.getState().sortOrder;
  }

  getItemKeyForKeyword (keyword: string): string | null {
    return keyword;
  }

  getSelection (): Immutable.Set<string> {
    return this.getState().selection;
  }

  getSelectedItems (): Immutable.Set<Item | null> {
    return this.getSelection().map((id: string) => this.getItem(id) ?? null);
  }

  getSelectionIndexes (): Immutable.Map<string, number> {
    return this.getState().selectionIndexes;
  }

  getAllowMultiSelect (): boolean {
    return this.getState().allowMultiSelect;
  }

  getEnsureSelectionIsVisible (): boolean {
    return this.getState().ensureSelectionIsVisible;
  }

  getItem (id: string): Item | undefined {
    return this.getState().mappedItems.get(id);
  }

  getName (id: string): string | undefined {
    const itemForId: Item | undefined = this.getItem(id);
    return itemForId != null ? itemForId.name : undefined;
  }

  // If a subclass uses another field for the selection instead of id, then that class should
  // provide it's own _selectionExistsInItems() method, filtering for the correct member.
  _selectionExistsInItems (id: string, items: Immutable.List<Item>): boolean {
    const index = items.findIndex((item) => item.id === id);
    return index !== -1;
  }

  _getUpdatedSelectionIndexes (items: Immutable.List<Item>, selection: Immutable.Set<string>): Immutable.Map<string, number> {
    // determine selection indexes
    let selectionIndexes: Immutable.Map<string, number> = Immutable.Map();

    for (const id of selection) {
      const index: number = items.findIndex((item: Item) => item.id === id);

      selectionIndexes = selectionIndexes.set(id, index);
    }

    return selectionIndexes;
  }

  _processItems (items: Immutable.List<Item>, sortOrder: SortOrder, filter: Filter):
   {items: Immutable.List<Item>, countPerGroup: Immutable.List<GroupAndCount>} {
    // filter and sort the items
    items = this._sortItems(items, sortOrder);
    items = this._filterItems(items, filter);

    const countPerGroup: Immutable.List<GroupAndCount> = this._generateCountPerGroup(items, sortOrder);

    return {items: items, countPerGroup: countPerGroup};
  }

  _removeItems (state: ItemsState<Item, ExtraData>, ids: string[]): ItemsState<Item, ExtraData> {
    if (ids == null) {
      return state;
    }

    const newSelection: Immutable.Set<string> = state.selection.filter(
      (id: string) => !ids.includes(id)
    );

    const newItems: Immutable.List<Item> = state.items.filter(
      (item: Item) => (
        !ids.includes(item.id)
      )
    );

    let mappedItems: Immutable.Map<string, Item> = state.mappedItems;
    for (const id of ids) {
      mappedItems = mappedItems.delete(id);
    }

    if (newItems.size !== state.items.size) {
      return state
        .set('items', newItems)
        .set('selection', newSelection)
        .set('mappedItems', mappedItems);
    }

    return state;
  }

  _getInsertPosition (item: Item, items: Immutable.List<Item>, sortOrder: SortOrder): number | null {
    const itemKey: string | null = this.getItemKeyForKeyword(sortOrder.keyword);

    if (itemKey) {
      const comparefunction: ((a: Item, b: Item) => number) | null = this._getCompareFunction(sortOrder);

      if (comparefunction != null) {
        const _comparefunction: (a: Item, b: Item) => number = comparefunction;

        let itemIndex: number = 0;

        for (; itemIndex < items.size; itemIndex++) {
          const currentItem: Item | undefined = items.get(itemIndex);
          if (currentItem != null) {
            const _currentItem: Item = currentItem;
            if (_comparefunction(item, _currentItem) < 0) {
              break;
            }
          }
        }
        return itemIndex;
      }
    }
    return null;
  }

  getValue (item: Item, itemKey: string) {
    return item.get(itemKey);
  }

  _updateItem (state: ItemsState<Item, ExtraData>, newItem: Item): ItemsState<Item, ExtraData> {
    let updatedState: ItemsState<Item, ExtraData> = state;

    const indexToUpdate: number = updatedState.items.findIndex((item: Item) => item !== undefined && item !== null && item.id === newItem.id);
    const checker = new FilterChecker(this.getFilterKeywords(), (keyword: string) => this.getItemKeyForKeyword(keyword), null, (item: Item, itemKey: string) => this.getValue(item, itemKey));

    if (indexToUpdate !== -1) {
      if (!Immutable.is(updatedState.items.get(indexToUpdate), newItem)) {
        if (checker.itemConformsToFilters(newItem, updatedState.filter, updatedState.advancedFilter)) {
          updatedState = updatedState
            .set('mappedItems', updatedState.mappedItems
              .set(newItem.id, newItem)
          );

          // item needs to be updated, find out if it needs to be replaced or added on another position
          const insertPosition: number | null = this._getInsertPosition(newItem, updatedState.items, updatedState.sortOrder);

          if (insertPosition != null) {
            if (insertPosition === -1) {
              updatedState = updatedState.set('items', updatedState.items.set(indexToUpdate, newItem));
            } else {
              updatedState = updatedState.set('items', state.items.remove(indexToUpdate));

              const newInsertPosition: number | null = this._getInsertPosition(newItem, updatedState.items, updatedState.sortOrder);

              if (newInsertPosition != null) {
                updatedState = updatedState.set('items', updatedState.items.insert(newInsertPosition, newItem));
              }
            }
          }
        } else {
          updatedState = updatedState.set('items', updatedState.items.remove(indexToUpdate));

          if (updatedState.selection.includes(newItem.id)) {
            updatedState = updatedState.set('selection', updatedState.selection.remove(newItem.id));
          }
        }
      } // else do nothing, item is not changed
    } else {
      if (checker.itemConformsToFilters(newItem, updatedState.filter, updatedState.advancedFilter)) {
        updatedState = updatedState
          .set('mappedItems', state.mappedItems
            .set(newItem.id, newItem)
        );

        // item needs to be added, find where it needs to be inserted
        const insertPosition: number | null = this._getInsertPosition(newItem, updatedState.items, updatedState.sortOrder);

        if (insertPosition != null) {
          updatedState = updatedState.set('items', updatedState.items.insert(insertPosition, newItem));
        }
      } // else do nothing, item is not changed
    }

    return updatedState;
  }

  addMoreIdsToRemove(state: ItemsState<Item, ExtraData>, idsToRemove: string[]) {
  }

  _updateItems (state: ItemsState<Item, ExtraData>, actions: Immutable.List<UpdateAction>): ItemsState<Item, ExtraData> {
    let updatedState: ItemsState<Item, ExtraData> = state;

    let idsToRemove: string[] = [];
    let itemsToAddOrUpdate: Item[] = [];

    for (let updateAction of actions) {
      if (updateAction.action === 'added') {
        itemsToAddOrUpdate.push(...updateAction.items);
      } else if (updateAction.action === 'modified') {
        itemsToAddOrUpdate.push(...updateAction.items);
      } else if (updateAction.action === 'removed') {
        idsToRemove.push(...updateAction.ids);
      }
    }

    for (let itemToAddOrUpdate of itemsToAddOrUpdate) {
      if (itemToAddOrUpdate) {
        updatedState = this._updateItem(updatedState, itemToAddOrUpdate);
      }
    }

    this.addMoreIdsToRemove(updatedState, idsToRemove);

    if (idsToRemove.length > 0) {
      updatedState = this._removeItems(updatedState, idsToRemove);
    }

    return updatedState;
  }

  _setSortOrder (state: ItemsState<Item, ExtraData>, newSortOrder: SortOrder): ItemsState<Item, ExtraData> {
    if (!Immutable.is(state.sortOrder, newSortOrder)) {
      // sortOrder changed (no selection change)
      let itemData: {items: Immutable.List<Item>, countPerGroup: Immutable.List<GroupAndCount>} =
      this._processItems(state.mappedItems.toList(), newSortOrder, state.filter);

      return state
        .set('sortOrder', newSortOrder)
        .set('items', itemData.items)
        .set('countPerGroup', itemData.countPerGroup);
    }
    return state;
  }

  _hasListeners (): boolean {
    return this.__emitter.listeners(this.__changeEvent).length > 0;
  }

  override reduce (state: ItemsState<Item, ExtraData>, action: ActionType): ItemsState<Item, ExtraData> {
    const hasListeners: boolean = this._hasListeners();

    if (!hasListeners && this._isStoreActive) {
      this._isStoreDirty = true;
    }

    this._isStoreActive = hasListeners;

    switch (action.actionType) {
      case undefined: {
        throw Error('Action is undefined.');
      }
      case this.Constants.SET_VISIBLE_RANGE: {
        if (!state.isFetching && (state.hasNotFetchedYet || this._isStoreDirty)) {
          return this._fetchAll(state.set('ensureSelectionIsVisible', false));
        }
        return state.set('ensureSelectionIsVisible', false);
      }
      case AllItemsConstants.REFRESH:
      case this.Constants.REFRESH: {
        return this._refresh(state);
      }
      case this.Constants.GET: {
        return state
          .set('items', state.items.clear())
          .set('mappedItems', state.mappedItems.clear())
          .set('isFetching', true)
          .set('hasNotFetchedYet', false);
      }
      case this.Constants.GET_SUCCESS: {
        if (this._isStoreActive) {
          this._isStoreDirty = false;
        }

        // copy received items to mapped items
        let mappedItems: Immutable.Map<string, Item> = Immutable.Map();
        if (action.items) {
          action.items.forEach((item: Item) => {
            mappedItems = mappedItems.set(item.id, item);
          });
        }

        // generate items and counterPerGroup based on the mapped items and the sorting and filtering
        let itemData: {items: Immutable.List<Item>, countPerGroup: Immutable.List<GroupAndCount>} =
         this._processItems(mappedItems.toList(), state.sortOrder, state.filter);

        // adapt selection
        const selectionExistsInItems: (id: string) => boolean = (id: string) => {
          return this._selectionExistsInItems(id, itemData.items);
        };

        const newSelection: Immutable.Set<string> = state.selection.filter(selectionExistsInItems);
        const newSelectionIndexes: Immutable.Map<string, number> = this._getUpdatedSelectionIndexes(itemData.items, newSelection);

        return state
          .set('selection', newSelection)
          .set('selectionIndexes', newSelectionIndexes)
          .set('items', itemData.items)
          .set('mappedItems', mappedItems)
          .set('countPerGroup', itemData.countPerGroup)
          .set('isFetching', false)
          .set('hasReceivedInitialData', true);
      }
      case this.Constants.GET_FAIL: {
        if (this._isStoreActive) {
          this._isStoreDirty = false;
        }

        return state
          .set('items', state.items.clear())
          .set('mappedItems', state.mappedItems.clear())
          .set('isFetching', false)
          .set('hasReceivedInitialData', true);
      }
      case this.Constants.RELOAD: {
        if (this._isStoreActive && !state.isFetching) {
          return this._fetchAll(state)
            .set('mappedItems', state.mappedItems.clear())
            .set('items', state.items.clear())
            .set('filter', new Filter())
            .set('filterString', '')
            .set('filterTemp', new Filter())
            .set('filterStringTemp', '')
            .set('sortOrder', this.getInitialSortOrder())
            .set('hasReceivedInitialData', false);
        }
        return state;
      }
      case this.Constants.SET_FILTER: {
        if (action.filterMode === FilterMode.GALLERY) {
          if (!Immutable.is(state.filterGallery, action.filter)) {
            return state
              .set('filterGallery', action.filter)
              .set('filterStringGallery', action.filterString);
          }
        } else if (action.filterMode === FilterMode.TEMP) {
          if (!Immutable.is(state.filterTemp, action.filter)) {
            return state
              .set('filterTemp', action.filter)
              .set('filterStringTemp', action.filterString);
          }
        }

        return state;
      }
      case this.Constants.ACTIVATE_FILTER: {
        let filter: Filter = new Filter();
        let filterString: string = '';

        if (action.filterMode === FilterMode.GALLERY) {
          filter = state.filterGallery;
          filterString = state.filterStringGallery;
        } else if (action.filterMode === FilterMode.TEMP) {
          filter = state.filterTemp;
          filterString = state.filterStringTemp;
        }

        if (!Immutable.is(state.filter, filter)) {
          // filter changed
          // generate items and counterPerGroup based on the mapped items and the sorting and filtering
          let itemData: {items: Immutable.List<Item>, countPerGroup: Immutable.List<GroupAndCount>} =
          this._processItems(state.mappedItems.toList(), state.sortOrder, filter);

          // adapt selection
          const selectionExistsInItems: (id: string) => boolean = (id: string) => {
            return this._selectionExistsInItems(id, itemData.items);
          };

          const newSelection: Immutable.Set<string> = state.selection.filter(selectionExistsInItems);
          const newSelectionIndexes: Immutable.Map<string, number> = this._getUpdatedSelectionIndexes(itemData.items, newSelection);

          return state
            .set('filter', filter)
            .set('filterString', filterString)
            .set('selection', newSelection)
            .set('selectionIndexes', newSelectionIndexes)
            .set('items', itemData.items)
            .set('countPerGroup', itemData.countPerGroup);
        }
        return state;
      }
      case this.Constants.SET_SORTORDER: {
        return this._setSortOrder(state, action.sortOrder);
      }
      case this.Constants.SET_SELECTION: {
        const selectionIndexes: Immutable.Map<string, number> = this._getUpdatedSelectionIndexes(state.items, action.selection);
        return state.set('selection', action.selection)
          .set('ensureSelectionIsVisible', !!action.ensureSelectionIsVisible && action.selection.size === 1)
          .set('selectionIndexes', selectionIndexes);
      }
      case this.Constants.MODIFY_SELECTION: {
        if (state.allowMultiSelect) {
          // multi-select enabled

          const newSelection: Immutable.Set<string> = (
            state.selection.includes(action.id)
            ? state.selection.delete(action.id)
            : state.selection.add(action.id)
          );

          const selectionIndexes: Immutable.Map<string, number> = this._getUpdatedSelectionIndexes(state.items, newSelection);

          return state.set('selection', newSelection)
            .set('ensureSelectionIsVisible', false)
            .set('selectionIndexes', selectionIndexes);
        } else {
          // multi-select not enabled
          if (state.selection.has(action.id)) {
            return state.set('selection', Immutable.Set())
              .set('ensureSelectionIsVisible', false)
              .set('selectionIndexes', Immutable.Map());
          } else {
            const newSelection: Immutable.Set<string> = Immutable.Set([action.id]);

            const selectionIndexes: Immutable.Map<string, number> = this._getUpdatedSelectionIndexes(state.items, newSelection);

            return state.set('selection', newSelection)
              .set('ensureSelectionIsVisible', !!action.ensureSelectionIsVisible)
              .set('selectionIndexes', selectionIndexes);
          }
        }
      }
      case this.Constants.ALLOW_MULTI_SELECT: {
        return state.set('allowMultiSelect', action.allow);
      }
      case this.Constants.OPEN:
      case this.Constants.GET_FOR_OPEN_SUCCESS: {
        if (action.item != null) {
          // when opening an item, set it as selected
          const selection: Immutable.Set<string> = Immutable.Set([action.item.id]);
          const updatedSelectionIndexes = this._getUpdatedSelectionIndexes(
            state.items,
            selection
          );

          return state
            .set('selection', selection)
            .set('selectionIndexes', updatedSelectionIndexes);
        }

        return state;
      }
      case this.Constants.REMOVE_SUCCESS: {
        if (!action.ids) {
          return state;
        }

        // Item was removed, so clear local items and reload from server.
        if (this._isStoreActive && !this.getState().isFetching) {
          state = this._fetchAll(state)
            .set('items', state.items.clear())
            .set('mappedItems', state.mappedItems.clear())
            .set('selection', Immutable.Set());
        }

        return state;
      }
      case this.Constants.SAVE_SUCCESS: {
        if (!state.mappedItems.has(action.item.id)) {
          // It's a new item, so clear local items and reload from server.
          if (this._isStoreActive && !this.getState().isFetching) {
            state = this._fetchAll(state)
              .set('items', state.items.clear())
              .set('mappedItems', state.mappedItems.clear())
          }
        } else {
          // update the item
          state = state.set('mappedItems', state.mappedItems.set(action.item.id, action.item));
          const index = state.items.findIndex((item: Item) => item.id === action.item.id);
          if (index !== -1) {
            state = state
              .set('items', state.items
                .set(index, action.item)
            );
          }
        }
        const newSelection: Immutable.Set<string> = Immutable.Set([action.item.id]);
        const selectionIndexes: Immutable.Map<string, number> = this._getUpdatedSelectionIndexes(state.items, newSelection);

        return state
          .set('selection', Immutable.Set([action.item.id]))
          .set('ensureSelectionIsVisible', true)
          .set('selectionIndexes', selectionIndexes);
      }
      case this.Constants.ENSURE_SELECTION_IS_VISIBLE: {
        return state.set('ensureSelectionIsVisible', action.ensureSelectionIsVisible);
      }
      case this.Constants.CLEAR: {
        return state
          .set('items', state.items.clear())
          .set('mappedItems', state.mappedItems.clear())
          .set('isFetching', false);
      }
      case this.Constants.UPDATE: {
        return this._updateItems(state, action.actions);
      }
      case UserPreferencesConstants.CLEAR_SUCCESS: {
        return this._setSortOrder(state, this.getInitialSortOrder());
      }
      default: {
        return state;
      }
    }
  }
}

export default ItemsStore;
